import React from 'react';
import { useQuery } from '@apollo/client';
import withStyles from 'isomorphic-style-loader/withStyles';
import s from './MarketplaceHome.css';
import getEstimatorJobs from '../../components/EstimatorDropdown/getEstimatorJobs.graphql';
import { RED, INK, GREY, BORDER, wrap } from '../seo/SeoUI';
import FaqAccordion from '../../components/Faq/FaqAccordion';
import { CUSTOMER_FAQS } from '../../components/Faq/faqData';
import { encode } from '../../helpers/queryEncryption';
import clientQr from './clientQrDataUri';
import HeroAiSearch from '../../components/AiAssistant/HeroAiSearch';

// BestHand customer app store listings (for the "Get the app" section).
const CLIENT_APP_STORE = 'https://apps.apple.com/us/app/besthand/id6759716088';
const CLIENT_PLAY_STORE =
  'https://play.google.com/store/apps/details?id=com.worldtechnologiessolutionsllc.besthand&hl=en_US';

const APP_BENEFITS = [
  ['M9 11l3 3L22 4M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11', "You're always in control", 'Book, manage, and track every job from your phone. You decide what, when, and who.'],
  ['M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6', 'Pay only for time you approve', 'No surprise charges — ever. You’re billed only for the hours you accept, with upfront pricing.'],
  // COMPLIANCE: background-check claim is intentional (founder confirms BestHand performs background checks). LEGAL_REVIEW_REQUIRED before prod.
  ['M12 2 4 5v6c0 5 3.4 8.5 8 11 4.6-2.5 8-6 8-11V5l-8-3ZM9 12l2 2 4-4', 'Verified pros in 60 min or less', 'Every pro is background-checked and skilled — and can be at your door within an hour.'],
  ['M12 7v5l3 2m6-2a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z', 'Book in under a minute', 'Tell us what you need, get matched instantly, and pick a time that suits you.'],
  ['M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10ZM9.5 12l1.8 1.8L15 10', 'Secure payments by Stripe', 'Your card is protected by Stripe. Pay safely in-app, released only when the job’s done right.'],
  ['M8 7V3m8 4V3M4 11h16M5 5h14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2Z', 'Reschedule free, anytime', 'Plans changed? Move your booking to another time at no extra cost.'],
  ['M12 21c-4.5-3-8-6-8-10a8 8 0 0 1 16 0c0 4-3.5 7-8 10ZM9 10h6', 'We support elders & veterans', 'A special assistance program for seniors and aging veterans — because help should reach everyone.'],
  ['M20 12v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-6m9-9 .9 2.7L20 6l-2.2 2 .6 3-3.4-1.8L11.6 11l.6-3L10 6l3.1-.3Z', 'Earn $100 + referral credits', 'Get $100 when you start, plus credits toward your next jobs for every friend you refer.'],
];

const TOP_CITIES = [
  ['New York', 'new-york-ny'], ['Los Angeles', 'los-angeles-ca'], ['Chicago', 'chicago-il'],
  ['Houston', 'houston-tx'], ['Phoenix', 'phoenix-az'], ['Dallas', 'dallas-tx'],
];
const SEO_SERVICES = [
  ['Cleaning', 'cleaning'], ['Plumbing', 'plumbing'], ['TV Mounting', 'tv-mounting'],
  ['Moving Help', 'moving-help'], ['Junk Removal', 'junk-removal'],
];

const slugify = (str) => (str || '').toString().toLowerCase().trim().replace(/&/g, 'and').replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
// Where a category card / search should go: its SEO page if it has one, otherwise straight to the
// booking flow pre-selected on that category (so EVERY category is reachable — no dead "Page Not Found").
const catHref = (c) => (c && c.seoSlug)
  ? `/services/${c.seoSlug}`
  : `/category?type=${encodeURIComponent(encode(String((c && c.id) || '')))}`;
// The 8 top-category chips shown under the hero search bar: [display label, category slug to link].
const TOP_CHIPS = [
  ['Plumbing Help', 'plumbing-help'],
  ['Electrical Help', 'electrical-help'],
  ['Yard Work', 'yard-work'],
  ['Trash & Junk Removal', 'trash-and-junk-removal'],
  ['Moving Help', 'general-moving'],
  ['Cleaning', 'cleaning'],
  ['TV Mounting', 'tv-s-mounting'],
  ['A/C & HVAC', 'hvac-a-c'],
];
// Image cards: the 8 chips + 2 more to fill the grid row.
const TOP_CARDS = [
  ...TOP_CHIPS,
  ['Painting', 'painting'],
  ['Furniture Assembly', 'furniture-assembly'],
  ['Beauty & Personal Care hair braiding', 'beauty-personal-care-hair-braiding'],
];
const fmt = (n) => (n >= 1000 ? Math.round(n).toLocaleString() : `${n}`);

// ── Rotating photo-banner hero slides ──────────────────────────────────────
// Placeholder branded SVGs in /images/hero (top-level `images/` is served at /images).
// Swap `src` for a licensed photo later — keep the same shape (src/alt/caption).
// `theme` tags renovation/large-project slides so the small-jobs-only state can use a
// quick-jobs-friendly subset (porch stays neutral enough; renos imply big projects).
const HERO_SLIDES = [
  { src: '/images/hero/hero-kitchen-renovation.jpg', alt: 'A freshly renovated modern kitchen with new cabinets and an island', caption: 'Kitchen remodel · Los Angeles, CA', theme: 'project' },
  { src: '/images/hero/hero-bathroom-renovation.jpg', alt: 'A renovated bathroom with a new vanity, tiled walls and a freestanding tub', caption: 'Bathroom renovation · Dallas, TX', theme: 'project' },
  { src: '/images/hero/hero-porch-renovation.jpg', alt: 'A refreshed front porch with new columns, railing and a red front door', caption: 'Porch refresh · Phoenix, AZ', theme: 'quick' },
  { src: '/images/hero/hero-hvac-installation.jpg', alt: 'A newly installed HVAC system with indoor unit and outdoor condenser', caption: 'A/C install · Miami, FL', theme: 'quick' },
  { src: '/images/hero/hero-electrical-installation.jpg', alt: 'An electrical panel and light fixtures being installed by a pro', caption: 'Electrical work · Chicago, IL', theme: 'quick' },
];
const PinIcon = () => (<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 21s-7-5.5-7-11a7 7 0 0 1 14 0c0 5.5-7 11-7 11Z" stroke="currentColor" strokeWidth="2" /><circle cx="12" cy="10" r="2.4" stroke="currentColor" strokeWidth="2" /></svg>);

// "Describe your problem" example prompts shown as the search field's placeholder. HeroAiSearch
// TYPES them out one char at a time (typewriter effect), then deletes and moves to the next, looping
// forever — teaching the client they can type a real-world problem (not just a service name). The
// whole array is passed down; the animation is owned inside HeroAiSearch. The field still routes into
// the quick-jobs flow on submit — these are guidance only.
const HERO_PROMPTS = [
  'I bought a new TV and want to mount it on the wall',
  'I have a water leak in my bathroom',
  'My AC stopped working',
  'I need my living room painted',
  'A power outlet stopped working',
  'My faucet keeps dripping',
];

// Self-contained rotating photo banner: 2-layer crossfade, 10s auto-advance, dot
// indicators, rotating caption chip, AA dark overlay + left scrim, two equal CTAs.
// Renders cleanly in BOTH flag states (small-jobs-only vs. full two-path).
function HeroBanner({ proofCount, largeProjectsEnabled, cats = [] }) {
  // Reduced-motion users get a single static frame (no auto-rotation), but dots still work.
  const prefersReduced =
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Small-jobs-only state: drop renovation/large-project slides so the hero never
  // implies a service we're not offering. Keep at least one slide.
  const slides = largeProjectsEnabled
    ? HERO_SLIDES
    : (HERO_SLIDES.filter((sl) => sl.theme === 'quick').length
      ? HERO_SLIDES.filter((sl) => sl.theme === 'quick')
      : HERO_SLIDES);

  const [idx, setIdx] = React.useState(0);
  const timer = React.useRef(null);
  // Restart the 10s auto-advance from now (called on mount and on every manual dot tap).
  const restart = React.useCallback(() => {
    if (timer.current) { clearInterval(timer.current); timer.current = null; }
    if (prefersReduced || slides.length <= 1) return;
    timer.current = setInterval(() => {
      // Pause while the tab is hidden — saves work and avoids a "jump" on return.
      if (typeof document !== 'undefined' && document.hidden) return;
      setIdx((i) => (i + 1) % slides.length);
    }, 10000);
  }, [prefersReduced, slides.length]);

  React.useEffect(() => {
    restart();
    const onVis = () => { if (typeof document !== 'undefined' && !document.hidden) restart(); };
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVis);
    return () => {
      if (timer.current) clearInterval(timer.current);
      if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVis);
    };
  }, [restart]);

  const goTo = (i) => { setIdx(i); restart(); };
  const active = slides[idx] || slides[0];

  return (
    <section className={s.bannerHero} aria-label="Featured BestHand projects">
      {/* Two stacked image layers — only the active one is at opacity 1 (true crossfade). */}
      {slides.map((sl, i) => (
        <img
          key={sl.src}
          className={`${s.bannerImg} ${i === idx ? s.on : ''}`}
          src={sl.src}
          alt={i === idx ? sl.alt : ''}
          aria-hidden={i === idx ? undefined : 'true'}
          // First image is the LCP: eager + high priority; the rest lazy-load.
          loading={i === 0 ? 'eager' : 'lazy'}
          fetchpriority={i === 0 ? 'high' : undefined}
          decoding="async"
          draggable="false"
        />
      ))}
      <div className={s.bannerScrim} />
      <div className={`${s.wrap} ${s.bannerInner}`}>
        <div className={s.bannerHead}>
          <div className={s.bannerTagline}>Home repairs, in expert hands.</div>
          <span className={s.bannerProof}><span className={s.pdot} />{fmt(proofCount)} verified pros &amp; companies near you</span>
          <h1 className={s.bannerH1}>
            {/* Rotating problem line (pure CSS crossfade, SSR-safe: all words render,
                keyframes cycle them; reduced-motion shows the first only). Screen
                readers get one static sentence instead of the animation. */}
            <span className={s.srOnly}>Something broke? A verified pro gets it fixed today.</span>
            <span className={s.rotStack} aria-hidden="true">
              {['Leaky faucet?', 'TV to mount?', 'Clogged drain?', 'Something broke?'].map((w, i) => (
                <span key={w} className={s.rotWord} style={{ animationDelay: `${i * 3}s` }}>{w}</span>
              ))}
            </span>
            <span className={s.rotFixed}>Get it fixed — today.</span>
          </h1>
          <p className={s.bannerSub}>
            A verified, background-checked pro at your door in under an hour — at fair, upfront prices. Every job backed by the BestHand Guarantee.
          </p>
        </div>

        {/* ONE SMART ENTRY (competitor-pattern single bar, plus our twist: the AI search
            routes free text into the pre-filled booking flow — the visitor never has to
            classify their own job). Large Projects keeps a one-line entry under the bar
            instead of half the hero; the two-paths section below still explains both. */}
        <div className={s.heroSearch}>
          <div className={s.bannerSearchCard}>
            <HeroAiSearch prompts={HERO_PROMPTS} />
          </div>
        </div>

        <div className={s.heroTrustRow}>
          <span className={s.heroTrustItem}>✓ Verified &amp; background-checked</span>
          <span className={s.heroTrustItem}>✓ Insured</span>
          <span className={s.heroTrustItem}>✓ Upfront pricing</span>
          <span className={s.heroTrustItem}>✓ BestHand Guarantee</span>
        </div>
      </div>
      {slides.length > 1 && (
        <div className={s.bannerDots} role="group" aria-label="Choose featured project">
          {slides.map((sl, i) => (
            <button
              key={sl.src}
              type="button"
              className={`${s.bannerDot} ${i === idx ? s.on : ''}`}
              aria-label={`Show project ${i + 1} of ${slides.length}`}
              aria-current={i === idx ? 'true' : undefined}
              onClick={() => goTo(i)}
            />
          ))}
        </div>
      )}
    </section>
  );
}

// A11Y: these are decorative icons (always paired with visible text), so they are
// marked aria-hidden to avoid noise for screen-reader users.
const Star = () => (<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="m12 2 3 6.3 6.9 1-5 4.9 1.2 6.8L12 17.8 5.9 21l1.2-6.8-5-4.9 6.9-1z" /></svg>);
const Arrow = () => (<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>);
const Wrench = () => (<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M7 11V6.5a1.5 1.5 0 0 1 3 0V11m0 0V4.5a1.5 1.5 0 0 1 3 0V11m0 0V5.5a1.5 1.5 0 0 1 3 0V12m0-1.5a1.5 1.5 0 0 1 3 0V15a6 6 0 0 1-6 6h-1.6a5 5 0 0 1-3.8-1.74l-3.2-3.8a1.6 1.6 0 0 1 2.3-2.2L7 14.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>);

function AppStoreBadge({ cls }) {
  return (
    <a className={cls} href={CLIENT_APP_STORE} target="_blank" rel="noopener noreferrer">
      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style={{ width: 26, height: 26, flex: '0 0 auto' }}>
        <path d="M16.4 12.8c0-2.2 1.8-3.3 1.9-3.4-1-1.5-2.6-1.7-3.2-1.7-1.4-.1-2.7.8-3.3.8-.7 0-1.7-.8-2.8-.8-1.5 0-2.8.8-3.6 2.1-1.5 2.7-.4 6.6 1.1 8.8.7 1 1.6 2.2 2.7 2.2 1.1 0 1.5-.7 2.8-.7s1.6.7 2.8.7c1.1 0 1.9-1 2.6-2.1.8-1.2 1.2-2.4 1.2-2.5-.1 0-2.2-.9-2.2-3.4Zm-2.2-6.2c.6-.7 1-1.7.9-2.7-.9 0-1.9.6-2.5 1.3-.5.6-1 1.6-.9 2.6 1 .1 1.9-.5 2.5-1.2Z" />
      </svg>
      <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1, textAlign: 'left' }}>
        <small style={{ fontSize: 11, opacity: 0.85 }}>Download on the</small>
        <strong style={{ fontSize: 18, fontWeight: 700 }}>App Store</strong>
      </span>
    </a>
  );
}

function PlayStoreBadge({ cls }) {
  return (
    <a className={cls} href={CLIENT_PLAY_STORE} target="_blank" rel="noopener noreferrer">
      <svg viewBox="0 0 24 24" aria-hidden="true" style={{ width: 26, height: 26, flex: '0 0 auto' }}>
        <path fill="#fff" d="m13.5 12 5.8-3.3-12.7-7c-.3-.2-.7-.3-1-.2L13.5 12Z" />
        <path fill="#fff" d="M4.6 1.5c-.3.3-.5.7-.5 1.2v18.6c0 .5.2.9.5 1.2l9-10.5-9-10.5Z" />
        <path fill="#fff" d="m13.5 12 3.6 3.6 4.3-2.4c.6-.4.6-1.2 0-1.6l-4.3-2.4L13.5 12Z" />
        <path fill="#fff" d="m4.6 22.5 8.9-10.5 3.6 3.6-11.5 7c-.3.2-.7.1-1-.1Z" />
      </svg>
      <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1, textAlign: 'left' }}>
        <small style={{ fontSize: 11, opacity: 0.85 }}>GET IT ON</small>
        <strong style={{ fontSize: 18, fontWeight: 700 }}>Google Play</strong>
      </span>
    </a>
  );
}

// Served metros (one per open state) for the manual area picker — used when IP geolocation can't
// place the visitor, so the "near you" rail is never left guessing or showing the whole country.
const SERVED_CITIES = [
  { city: 'Los Angeles', state: 'CA', lat: 34.0522, lng: -118.2437 },
  { city: 'Phoenix', state: 'AZ', lat: 33.4484, lng: -112.0740 },
  { city: 'Las Vegas', state: 'NV', lat: 36.1699, lng: -115.1398 },
  { city: 'Dallas', state: 'TX', lat: 32.7767, lng: -96.7970 },
  { city: 'Chicago', state: 'IL', lat: 41.8781, lng: -87.6298 },
  { city: 'Miami', state: 'FL', lat: 25.7617, lng: -80.1918 },
  { city: 'New York', state: 'NY', lat: 40.7506, lng: -73.9972 },
  { city: 'Philadelphia', state: 'PA', lat: 39.9526, lng: -75.1652 },
  { city: 'Newark', state: 'NJ', lat: 40.7357, lng: -74.1724 },
  { city: 'Seattle', state: 'WA', lat: 47.6062, lng: -122.3321 },
];

// Inline price estimator (Section 1 of BestHand-sections.html) — wired to the
// REAL estimator data (getEstimatorJobs: real per-hour minRate per category)
// with the SAME fee math as the header estimator (+10% labour + $10 visit).
// "Job size" maps to an hours range so we show a real low–high price range.
// Scrolling promo ticker — kept from prod (same top-of-home placement).
const PROMOS = [
  '🚨 Need it urgently? Background-checked pros available in under an hour',
  "🗓️ You're in control — book pros on your own schedule, anytime",
  '⭐ Book background-checked, top-rated pros near you',
  '🛡️ Every booking is backed by our satisfaction guarantee',
];

const EST_SIZES = [{ label: 'Small', lo: 1, hi: 2 }, { label: 'Medium', lo: 2, hi: 4 }, { label: 'Large', lo: 4, hi: 8 }];
const EST_FEE = 0.10;
const EST_VISIT = 10;
const r5 = (n) => Math.round(n / 5) * 5;

function InlineEstimator() {
  const { data, loading } = useQuery(getEstimatorJobs, { ssr: false });
  // Show these popular jobs first, IN THIS ORDER (matched by category name; only those that
  // actually have a price appear). Plumbing, TV mounting, Cleaning, Electrical, Furniture
  // assembly, Moving help, Yard work.
  const EST_PREFERRED = ['plumb', 'tv', 'clean', 'electric', 'furniture', 'moving', 'yard'];
  const priced = ((data && data.getEstimatorJobs && data.getEstimatorJobs.results) || [])
    .filter((j) => j && j.hasPrice && j.minRate > 0);
  const preferred = [];
  EST_PREFERRED.forEach((key) => {
    const m = priced.find((j) => (j.name || '').toLowerCase().includes(key) && !preferred.includes(j));
    if (m) preferred.push(m);
  });
  const jobs = (preferred.length ? preferred : priced).slice(0, 7);
  const [svcI, setSvcI] = React.useState(0);
  const [szI, setSzI] = React.useState(0); // default job size = Small (not Medium)
  const sv = jobs[svcI] || jobs[0] || null;
  const sz = EST_SIZES[szI];
  const sign = sv && sv.currency && sv.currency !== 'USD' ? '' : '$';
  const lo = sv ? r5(sv.minRate * sz.lo * (1 + EST_FEE) + EST_VISIT) : 0;
  const hi = sv ? r5(sv.minRate * sz.hi * (1 + EST_FEE) + EST_VISIT) : 0;
  const bookHref = sv ? `/category?type=${encodeURIComponent(encode(String(sv.categoryId)))}` : '/category?type=all';
  return (
    <div className={s.estCard}>
      <div className={s.estCardLbl}>What do you need?</div>
      <div className={s.estChips}>
        {(jobs.length ? jobs : [{ categoryId: 0, name: loading ? 'Loading…' : 'No jobs' }]).map((x, i) => (
          <button key={x.categoryId} type="button" className={`${s.estChip} ${i === svcI ? s.on : ''}`} onClick={() => setSvcI(i)} disabled={!jobs.length}>{x.name}</button>
        ))}
      </div>
      <div className={s.estCardLbl}>Job size</div>
      <div className={s.estSegs}>
        {EST_SIZES.map((x, i) => (
          <button key={x.label} type="button" className={`${s.estSeg} ${i === szI ? s.on : ''}`} onClick={() => setSzI(i)}>{x.label}</button>
        ))}
      </div>
      <div className={s.estResult}>
        <div>
          <div className={s.estResLbl}>Estimated price</div>
          <div className={s.estResMeta}>{sv ? `${sv.name} · ${sz.label}` : '—'}</div>
        </div>
        <div className={s.estRange}>{loading ? '…' : (sv ? `${sign}${lo}–${sign}${hi}` : '—')}</div>
      </div>
      <a className={s.estBook} href={bookHref}>Book this job <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" style={{ width: 16, height: 16 }}><path d="M5 12h14M13 6l6 6-6 6" /></svg></a>
      <div className={s.estFine}>Final price confirmed by your pro before you pay.</div>
    </div>
  );
}

function MarketplaceHome({ categories = [], pros = [], heroPool = [], reviews = [], stats = {}, served = true, area = null, nearbyCount = null, largeProjectsEnabled = false }) {
  // Manual area override: navigate to /?area=<ST>. The server maps it to a metro, persists bh_loc, and
  // redirects to a clean URL — robust (no client-side cookie/reload timing issues).
  const pickArea = (e) => {
    const st = e.target.value;
    if (st && typeof window !== 'undefined') window.location.href = `/?area=${encodeURIComponent(st)}`;
  };
  const AreaPicker = () => (
    <select onChange={pickArea} defaultValue="" style={{ padding: '8px 12px', borderRadius: 999, border: '1px solid var(--line)', background: '#fff', fontWeight: 600, fontSize: 14, color: 'var(--ink)', cursor: 'pointer' }}>
      <option value="" disabled>📍 Change area</option>
      {SERVED_CITIES.map((c) => <option key={c.state} value={c.state}>{c.city}, {c.state}</option>)}
    </select>
  );
  // REAL counts only — no padding/inflation (anti-FTC: never show a number bigger
  // than reality). The displayed home stats use jobsCategories / prosAndCompanies
  // below; these two are kept as honest real totals for any legacy consumer.
  const jobsCompleted = stats.jobsCompleted || 0;
  const verifiedPros = stats.verifiedPros || 0;
  const avgRating = stats.avgRating > 0 ? stats.avgRating : 4.9;
  const prosOnline = stats.prosOnline > 0 ? stats.prosOnline : verifiedPros;
  // Real, live counts for the stats line (no padding — grow on each new
  // sub-category / worker / company).
  const jobsCategories = stats.jobsCategories || 0;
  const prosAndCompanies = stats.prosAndCompanies || 0;
  // "{N} pros near you" = real count within the visitor's radius (grows live as workers join);
  // falls back to the global online count when location is unknown.
  // Hero proof chip — use the SAME real count as the stats line (no padded
  // number, so the hero never contradicts the "X pros & companies" stat).
  const nearYouCount = (typeof nearbyCount === 'number' && nearbyCount > 0) ? nearbyCount : prosAndCompanies;

  const cats = (categories || []).slice().sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));

  // Detect device so the app-download section shows the right store (QR only on desktop).
  const [os, setOs] = React.useState('desktop');
  React.useEffect(() => {
    const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
    if (/iPhone|iPad|iPod/i.test(ua)) setOs('ios');
    else if (/Android/i.test(ua)) setOs('android');
    else setOs('desktop');
  }, []);

  const steps = [
    ['Tell us what you need', 'Pick a service and describe your job in under a minute. No phone tag.'],
    ['Get matched with a pro', 'Compare vetted, reviewed local pros with upfront pricing — side by side.'],
    ['Book & relax', 'Schedule and pay securely in-app, every job backed by our guarantee.'],
  ];
  const why = [
    // COMPLIANCE: background-check claim is intentional (founder confirms BestHand performs background checks). LEGAL_REVIEW_REQUIRED before prod.
    ['Background-checked pros', 'Every pro completes a background check before they ever reach your door.',
      <path d="M12 2 4 5v6c0 5 3.4 8.5 8 11 4.6-2.5 8-6 8-11V5l-8-3Z M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round" fill="none" />],
    ['Upfront pricing', 'See the price before you book — no hourly surprises, no hidden fees.',
      <path d="M3 9h18M5 5h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />],
    ['Secure payments', 'Pay safely in-app, released only when the work is done right.',
      <path d="M3 6h18v13H3zM3 10h18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />],
    ['Satisfaction guarantee', "Not happy? We'll make it right or refund the job. Every time.",
      <path d="M12 3a5 5 0 1 1 0 10 5 5 0 0 1 0-10ZM10 12l1.4 1.4L14 11" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />],
  ];

  return (
    <div className={s.page}>
      {/* Scrolling promo ticker — kept from prod, top of home. */}
      <div className={s.wrap} style={{ paddingTop: 14 }}>
        <div className={s.promoBar}>
          <div className={s.promoTrack}>
            {PROMOS.concat(PROMOS).map((p, i) => <span className={s.promoItem} key={i}>{p}</span>)}
          </div>
        </div>
      </div>
      {/* ── ROTATING PHOTO-BANNER HERO (replaces the old search-hero) ── */}
      <HeroBanner proofCount={nearYouCount} largeProjectsEnabled={largeProjectsEnabled} cats={cats} />

      {/* POPULAR services + "two ways" intro — sits right above the two path tiles. */}
      <section className={s.pathsIntro}>
        <div className={s.wrap}>
          <div className={s.popularRow}>
            {['Plumbing', 'Cleaning', 'Electrical', 'Yard work', 'TV mounting', 'Moving help', 'Furniture assembly', 'Trash & junk removal'].map((label) => {
              const c = cats.find((x) => (x.name || '').toLowerCase().includes(label.toLowerCase().split(' ')[0]));
              const href = c ? `/category?type=${encodeURIComponent(encode(String(c.id)))}` : '/category?type=all';
              return <a key={label} className={s.popularPill} href={href}>{label}</a>;
            })}
            <a className={s.popularPill} href="/category?type=all">All services →</a>
          </div>
          <div className={s.pathsHead}>
            <span className={s.eyebrow}>Two ways to get it done</span>
            <h2 className={s.pathsTitle}>Not sure which one you need?</h2>
            <p className={s.pathsSub}>Quick, well-defined tasks get one pro at a fixed price. Bigger, multi-step work gets competing quotes from verified companies. Pick the side that matches your job.</p>
          </div>
        </div>
      </section>

      {/* ── TWO-PATHS CLARITY SECTION (two equal doors) ── */}
      <section className={s.paths} aria-label="Two ways to get help">
        <div className={s.wrap}>
          <div className={s.pathsGrid}>
            {/* Quick jobs — always shown. */}
            <div className={`${s.pathCard} ${s.quick}`}>
              <span className={`${s.pathTag} ${s.pathTagQuick}`}>Quick jobs</span>
              <h3>Get a small job done today</h3>
              <ul className={s.pathSteps}>
                <li><span className={s.pathNum}>1</span><span>Tell us what you need — in under a minute.</span></li>
                <li><span className={s.pathNum}>2</span><span>Get matched with a trusted local pro in ~60 seconds.</span></li>
                <li><span className={s.pathNum}>3</span><span>Book &amp; relax — pay securely, backed by our guarantee.</span></li>
              </ul>
              <a className={`${s.btn} ${s.btnPrimary} ${s.pathCta}`} href="/category?type=all">Book a pro</a>
            </div>
            {/* Large projects — only when the flag is ON (no dead card when OFF). */}
            {largeProjectsEnabled && (
              <div className={`${s.pathCard} ${s.big}`}>
                <span className={`${s.pathTag} ${s.pathTagBig}`}>Large projects</span>
                <h3>Get quotes for a bigger renovation</h3>
                <ul className={s.pathSteps}>
                  <li><span className={s.pathNum}>1</span><span>Describe your project — no on-site visit needed to start.</span></li>
                  <li><span className={s.pathNum}>2</span><span>Verified companies send competing quotes (identities masked).</span></li>
                  <li><span className={s.pathNum}>3</span><span>Choose &amp; pay safely by milestone — escrow-protected.</span></li>
                </ul>
                <a className={`${s.btn} ${s.btnGhost} ${s.pathCta}`} href="/post-a-project">Get project quotes</a>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ============================================================
          SECTION ORDER (per founder brief):
          SERVICES → COUNTERS → TESTIMONIALS → PROS → HOW → WHY
          → CTA → GET THE APP → FAQ → SEO. The standalone "Large
          projects" dark band was removed — the two-paths section
          above now covers that entry point (no redundant band).
          Each block below is the original markup, only reordered.
          ============================================================ */}

      {/* NEW — security/trust badges + ⭐ rating strip (added for A/B comparison; old kept). */}
      <section className={s.badgeStrip}>
        <div className={`${s.wrap} ${s.badgeStripIn}`}>
          <span className={s.badgeItem}><i className={s.badgeChk}>✓</i> Secure payment by <b>&nbsp;Stripe</b></span>
          <span className={s.badgeItem}><i className={s.badgeChk}>✓</i> Satisfaction guarantee</span>
          <span className={s.badgeItem}><i className={s.badgeChk}>✓</i> No hidden fees · no lead fees</span>
          <span className={s.badgeItem}><i className={s.badgeChk}>✓</i> Background-checked pros</span>
          <span className={s.badgeItem}><span className={s.badgeStar}>★</span> <b>{avgRating}</b>&nbsp;average rating</span>
        </div>
      </section>

      {/* COUNTERS (Stats) — moved to sit just above "Browse popular services". */}
      <div className={s.counters}>
        <div className={`${s.wrap} ${s.countersIn}`}>
          <div className={s.counter}><div className={s.num}>{fmt(jobsCategories)}<span className={s.u}>+</span></div><div className={s.lbl}>Bookable services</div></div>
          <div className={s.counter}><div className={s.num}>{fmt(prosAndCompanies)}<span className={s.u}>+</span></div><div className={s.lbl}>Verified pros &amp; companies near you</div></div>
          <div className={s.counter}><div className={s.num}>{avgRating}</div><div className={s.lbl}>Average rating</div></div>
          <div className={s.counter}><div className={s.num}>60<span className={s.u}>s</span></div><div className={s.lbl}>Avg. match time</div></div>
        </div>
      </div>

      {/* TRUST / INSURANCE — reassurance band (selling point), just above Browse services.
          COMPLIANCE: damage-protection insurance + background-check + insured-companies
          claims are intentional (founder confirms the coverage is in place).
          LEGAL_REVIEW_REQUIRED: confirm exact policy wording / limits / states before prod. */}
      <section className={s.sec} style={{ paddingBottom: 0 }}>
        <div className={s.wrap}>
          <div className={`${s.secHead} ${s.center}`}>
            <span className={s.eyebrow} style={{ fontSize: '26px' }}>Booked with confidence</span>
            <h2>You&apos;re protected on every job</h2>
          </div>
          <div className={s.trustGrid}>
            <div className={s.trustCard}>
              <div className={`${s.trustIc} ${s.trustIcRed}`}>
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 2 4 5v6c0 5 3.4 8.5 8 11 4.6-2.5 8-6 8-11V5l-8-3Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" /><path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </div>
              {/* COMPLIANCE / LEGAL_REVIEW_REQUIRED: "$5,000" damage-protection coverage claim — the exact
                  amount, eligibility, and wording must be confirmed by counsel + insurer terms before prod. */}
              <h3>Peace of Mind Protection</h3>
              <p>Every small job is backed by BestHand&apos;s Peace of Mind Protection — if a pro accidentally damages your property, you&apos;re covered up to&nbsp;$5,000, subject to the program terms. Our pros carry their own insurance too, so you&apos;re protected on two levels.</p>
            </div>
            <div className={s.trustCard}>
              <div className={`${s.trustIc} ${s.trustIcTeal}`}>
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" /><path d="M8.5 12l2.2 2.2 4.3-4.4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </div>
              <h3>Background-checked pros</h3>
              <p>Every pro completes identity verification and a background check before they reach your door.</p>
            </div>
            <div className={s.trustCard}>
              <div className={`${s.trustIc} ${s.trustIcTeal}`}>
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M3 21h18M5 21V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /><path d="M9 7h.01M9 11h.01M9 15h.01M15 7h.01M15 11h.01M15 15h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
              </div>
              <h3>Verified &amp; insured companies</h3>
              <p>Large projects are handled by licensed, insured &amp; verified companies — with milestone payments held in escrow.</p>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 1 — Instant estimate (BestHand-sections.html). */}
      <section className={s.estSec} id="estimate">
        <div className={s.wrap}>
          <div className={s.estGrid}>
            <div className={s.estLeft}>
              <span className={s.eyebrow}>Instant estimate</span>
              <h2 className={s.estTitle}>Know the price before you book</h2>
              <p className={s.estLede}>No waiting on callbacks or quotes. Pick your job, get a real price range in seconds, then book a pro in one tap.</p>
              <div className={s.estChecks}>
                <div className={s.estCheck}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg><span>Based on thousands of real completed jobs</span></div>
                <div className={s.estCheck}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg><span>Upfront pricing — confirmed before you pay</span></div>
                <div className={s.estCheck}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg><span>No hidden fees — one simple 10% fee, secure payment by Stripe</span></div>
              </div>
            </div>
            <InlineEstimator />
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className={s.sec} id="services">
        <div className={s.wrap}>
          <div className={s.secHead}>
            <div className={s.top}>
              <div>
                <span className={s.eyebrow} style={{ fontSize: '26px' }}>Browse services</span>
                <h2>Whatever the job, we&apos;ve got a pro</h2>
              </div>
            </div>
          </div>
          <div className={s.svcGrid}>
            {TOP_CARDS.map(([label, slug]) => {
              const c = cats.find((x) => x.slug === slug);
              if (!c) return null;
              return (
                <a key={slug} className={s.svc} href={catHref(c)}>
                  {c.logoImage
                    ? <img className={s.svcImg} src={`/images/category/${c.logoImage}`} alt={label} />
                    : <div className={s.svcFallback}><Wrench /></div>}
                  <div className={s.svcOverlay}>
                    <h3>{label}</h3>
                    <span className={s.find}>Find a pro <Arrow /></span>
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 2 — Home Resource Center teaser: home-care articles + cost guides.
          Serves the research-phase visitor (the majority) with a next step besides booking. */}
      <section className={s.guidesSec}>
        <div className={s.wrap}>
          <div className={s.guidesHead}>
            <div>
              <span className={s.eyebrow}>Home care & advice</span>
              <h2 className={s.guidesTitle}>Take care of your home like a pro</h2>
            </div>
            <a className={s.guidesSeeAll} href="/guides">Visit the Resource Center <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg></a>
          </div>
          <div className={s.guidesGrid}>
            {[
              ['Home care', 'The complete seasonal maintenance checklist', '🏠 4 seasons', '/guides/home-maintenance-checklist'],
              ['Home care', '12 fall tasks to do before the first freeze', '🍂 Timely', '/guides/fall-home-maintenance-checklist'],
              ['Home care', '10 small problems that become expensive repairs', '⚠️ Must-read', '/guides/10-small-problems-big-repairs'],
              ['Cost guide', 'How much does a handyman cost?', '$50–$125/hr', '/guides/handyman-cost'],
            ].map(([kicker, q, price, href]) => (
              <a className={s.guideCard} href={href} key={q}>
                <span className={s.guideKicker}>{kicker}</span>
                <span className={s.guideQ}>{q}</span>
                <span className={s.guidePrice}>{price}</span>
                <span className={s.guideRead}>Read guide <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg></span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      {reviews.length > 0 && (
        <section className={s.sec}>
          <div className={s.wrap}>
            <div className={`${s.secHead} ${s.center}`}>
              <span className={s.eyebrow} style={{ fontSize: 'clamp(22px, 6vw, 40px)' }}>What customers say</span>
              <h2>Loved by homeowners</h2>
            </div>
          </div>
          <div className={s.marquee}>
            <div className={s.marqueeTrack}>
              {reviews.concat(reviews).map((r, i) => (
                <div key={i} className={s.tcard}>
                  <div className={s.stars}>{[0, 1, 2, 3, 4].map((k) => <Star key={k} />)}</div>
                  <p className={s.qt}>&ldquo;{r.content || 'Great service, highly recommend!'}&rdquo;</p>
                  <div className={s.tBy}><div className={s.tAv} style={{ background: '#E8232B' }}>{(r.authorName || 'C')[0]}</div><div><div className={s.n}>{r.authorName}</div><div className={s.r}>{r.proName}</div></div></div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* PROS (location-aware rail) */}
      {pros.length > 0 && (
        <section className={s.sec} id="pros" style={{ paddingTop: 0 }}>
          <div className={s.wrap}>
            <div className={s.secHead}>
              <div className={s.top} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
                <div>
                  <span className={`${s.eyebrow} ${s.teal}`}>Top-rated {area ? `near ${area}` : 'near you'}</span>
                  <h2>Meet the pros behind the work</h2>
                </div>
                <AreaPicker />
              </div>
            </div>
            {/* Auto-scrolling marquee (Paul): the rail glides continuously; pause on hover so a card can be clicked. */}
            <div className={s.prosMarquee}>
              {/* Constant glide speed regardless of how many pros load: ~3s per card (matches the
                  original 20-cards-in-60s). Without this, removing the display cap sped the rail up. */}
              <div className={s.prosTrack} style={{ animationDuration: `${Math.max(60, pros.length * 3)}s` }}>
                {[...pros, ...pros].map((p, i) => {
                  const card = (
                    <React.Fragment>
                      <div className={s.proPhoto}>
                        {p.picture ? <img src={`/images/avatar/${p.picture}`} alt={p.name} /> : null}
                        {p.verified ? <div className={s.proVerify}><svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 2 4 5v6c0 5 3.4 8.5 8 11 4.6-2.5 8-6 8-11V5l-8-3Z" stroke="#0A7C70" strokeWidth="1.8" strokeLinejoin="round" /><path d="m9 12 2 2 4-4" stroke="#0A7C70" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>Verified</div> : null}
                      </div>
                      <div className={s.proBody}>
                        <div className={s.proTop}>
                          <div className={s.proAv} style={{ background: '#E8232B' }}>{p.picture ? <img src={`/images/avatar/${p.picture}`} alt={p.name} /> : (p.name || 'P')[0]}</div>
                          <div>
                            <div className={s.proName}>{p.name}</div>
                            {/* Card = photo + name + skill + price (Paul): no city, no rating. */}
                            <div className={s.proCat}>{p.topService || 'Local pro'}</div>
                          </div>
                        </div>
                        {p.fromPrice ? <div className={s.proMeta}><div className={s.proPrice}>from <b>${Math.round(p.fromPrice)}</b></div></div> : null}
                      </div>
                    </React.Fragment>
                  );
                  // Demo pros are display-only (non-clickable); real pros link out. Duplicated set → index key.
                  return p.isDemo
                    ? <div key={`${p.id}-${i}`} className={s.pro} style={{ cursor: 'default' }} aria-hidden={i >= pros.length ? 'true' : undefined}>{card}</div>
                    : <a key={`${p.id}-${i}`} className={s.pro} href={`/pro/${p.id}`} aria-hidden={i >= pros.length ? 'true' : undefined}>{card}</a>;
                })}
              </div>
            </div>
            <div style={{ textAlign: 'center', marginTop: 28 }}>
              <a className={s.heroProCta} href="/become-a-pro">Become a Worker <span aria-hidden="true">→</span></a>
              {/* LARGE PROJECTS FLAG: hide LP "Join as a company" CTA when OFF (additive). */}
              {largeProjectsEnabled && <a className={s.heroProCta} href="/become-a-company">Join as a company <span aria-hidden="true">→</span></a>}
            </div>
          </div>
        </section>
      )}

      {/* No regional pros: either we couldn't detect the area (let them pick) or we're not live there yet.
          Either way, NEVER show all-states here — keep the bottom strictly local. */}
      {pros.length === 0 && (
        <section className={s.sec} id="waitlist" style={{ paddingTop: 0 }}>
          <div className={s.wrap}>
            <div style={{ background: 'linear-gradient(135deg,#FFE3E1,#E4F6F3)', border: '1px solid var(--line)', borderRadius: 22, padding: '40px 28px', textAlign: 'center' }}>
              <span className={`${s.eyebrow} ${s.teal}`}>{area ? 'Expanding across the U.S.' : 'See pros near you'}</span>
              <h2 style={{ margin: '8px 0 10px' }}>{area ? `BestHand is coming to ${area}` : 'Choose your area to see local pros'}</h2>
              <p style={{ maxWidth: 560, margin: '0 auto 18px', color: 'var(--muted)' }}>
                {area
                  ? "We're not live here just yet. Get the app to be first in line when trusted local pros open up near you."
                  /* COMPLIANCE: background-check claim is intentional (founder confirms BestHand performs background checks). LEGAL_REVIEW_REQUIRED before prod. */
                  : "We couldn't detect your location. Pick your city to see background-checked pros near you."}
              </p>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', alignItems: 'center' }}>
                <AreaPicker />
                <a className={s.heroProCta} href="/get-app">Get the app <span aria-hidden="true">→</span></a>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* HOW IT WORKS */}
      <section className={s.sec} id="how" style={{ paddingTop: 0 }}>
        <div className={s.wrap}>
          <div className={s.how}>
            <div className={s.secHead} style={{ marginBottom: 0 }}>
              <span className={s.eyebrow} style={{ color: '#FF7A80', fontSize: 30, fontWeight: 800 }}>How it works</span>
              <h2>Book a trusted pro in three steps</h2>
              <p>From &ldquo;I need this fixed&rdquo; to &ldquo;done&rdquo; — usually the same day.</p>
            </div>
            <div className={s.steps}>
              {steps.map((st, i) => (
                <div key={i} className={s.step}><div className={s.stepLine} /><div className={s.stepN}>{i + 1}</div><h3>{st[0]}</h3><p>{st[1]}</p></div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className={s.sec} id="why" style={{ paddingTop: 0 }}>
        <div className={s.wrap}>
          <div className={`${s.secHead} ${s.center}`}>
            <span className={`${s.eyebrow} ${s.teal}`} style={{ fontSize: '26px' }}>Why BestHand</span>
            <h2>Peace of mind, built in</h2>
            <p>The trust and protection big platforms charge extra for — standard on every booking.</p>
          </div>
          <div className={s.whyGrid}>
            {why.map((w, i) => (
              <div key={i} className={s.why}>
                <div className={s.whyIc}><svg viewBox="0 0 24 24" fill="none" aria-hidden="true">{w[2]}</svg></div>
                <h3>{w[0]}</h3><p>{w[1]}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GIVING BACK — Seniors & Veterans program (design from BestHand-sections.html). */}
      <section className={s.giving} id="community">
        <div className={s.wrap}>
          <div className={s.givingGrid}>
            <div className={s.givingText}>
              <span className={s.eyebrow}>Giving back</span>
              <h2 className={s.givingTitle}>Seniors &amp; veterans program</h2>
              <p className={s.givingLede}>Our way of giving back to the community. Seniors and veterans get special rates, priority matching, and extra support — so the people who built and protected our neighborhoods always have a trusted hand to count on.</p>
              <div className={s.givingFeats}>
                {[
                  ['M12 1v22 M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6', 'Special discounted rates', 'Reduced pricing on every booking for eligible seniors and veterans.'],
                  ['M13 2 3 14h7l-1 8 11-13h-7l1-7z', 'Priority matching & support', 'Get matched faster and reach a real person whenever you need help.'],
                  ['M12 1l9 4v6c0 5-3.8 9.4-9 11-5.2-1.6-9-6-9-11V5z M9 12l2 2 4-4', 'Vetted, background-checked pros', 'Extra peace of mind — every pro is background-checked and insured.'],
                ].map(([d, t, sub]) => (
                  <div className={s.givingFeat} key={t}>
                    <span className={s.givingFeatIc}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d={d} /></svg></span>
                    <div>
                      <div className={s.givingFeatT}>{t}</div>
                      <div className={s.givingFeatS}>{sub}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className={s.givingCtas}>
                <a className={`${s.btn} ${s.btnPrimary} ${s.btnLg}`} href="/senior-veteran-discount">Apply for the program <span aria-hidden="true">→</span></a>
                <a className={`${s.btn} ${s.btnGhost} ${s.btnLg}`} href="/senior-veteran-discount">How to qualify</a>
              </div>
            </div>
            <div className={s.givingMediaWrap}>
              <div className={s.givingMedia}>
                <img src="/images/hero/hero-bathroom-renovation.jpg" alt="Giving back to seniors and veterans" />
                <div className={s.givingScrim} />
              </div>
              <div className={s.givingFloatTop}>
                <span className={s.givingFloatIc}><svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1l9 4v6c0 5-3.8 9.4-9 11-5.2-1.6-9-6-9-11V5z M9 12l2 2 4-4" /></svg></span>
                <div>
                  <div className={s.givingFloatT}>Veterans &amp; 65+</div>
                  <div className={s.givingFloatS}>Verified &amp; priority support</div>
                </div>
              </div>
              <div className={s.givingFloatBot}>
                <div className={s.givingRateLbl}>Member rate</div>
                <div className={s.givingRateBig}>Special pricing</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* (Pricing "No surprises" section merged into the Instant Estimate section
          above — its essence is now the 3rd check there. Removed to de-duplicate.) */}

      {/* GET THE APP — merged: phone-mockup design (BestHand-sections.html) with
          the real device-aware store badges + desktop QR (replaces the old appSec). */}
      <section className={s.appNew} id="app-mockup">
        <div className={s.wrap}>
          <div className={s.appNewGrid}>
            <div className={s.appNewText}>
              <span className={s.eyebrow}>Get the app</span>
              <h2 className={s.appNewTitle}>Everything you need, one tap away</h2>
              <p className={s.appNewLede}>Book trusted local pros, track your job in real time, and pay securely — all from your phone.</p>
              <div className={s.appNewFeats}>
                {[
                  ["You're always in control", 'Book, manage, and track every job from your phone. You decide what, when, and who.'],
                  ['Pay only for time you approve', 'No surprise charges — ever. Billed only for hours you accept, at an upfront price.'],
                  ['Background-checked pros in 60 min or less', 'Every pro is background-checked — and can be at your door within an hour.'],
                  ['Secure payments by Stripe', 'Your card is protected. Paid out only when the job is done right.'],
                ].map(([t, sub]) => (
                  <div className={s.appNewFeat} key={t}>
                    <span className={s.appNewFeatIc}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg></span>
                    <div><div className={s.appNewFeatT}>{t}</div><div className={s.appNewFeatS}>{sub}</div></div>
                  </div>
                ))}
              </div>
              <div className={s.appNewBadges}>
                {os === 'android'
                  ? <React.Fragment><PlayStoreBadge cls={s.appBadge} /><AppStoreBadge cls={s.appBadge} /></React.Fragment>
                  : <React.Fragment><AppStoreBadge cls={s.appBadge} /><PlayStoreBadge cls={s.appBadge} /></React.Fragment>}
              </div>
              {os === 'desktop' && (
                <div className={s.appQr} style={{ marginTop: 18 }}>
                  <img src={clientQr} alt="Scan to download the BestHand app" />
                  <span>Scan with your phone<br />to download the app</span>
                </div>
              )}
            </div>
            <div className={s.appNewPhoneWrap}>
              <div className={s.appNewPhone}>
                <div className={s.appNewScreen}>
                  <img src="/images/hero/hero-bathroom-renovation.jpg" alt="BestHand app preview" />
                  <div className={s.appNewCardLive}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ width: 38, height: 38, borderRadius: '50%', background: 'linear-gradient(135deg,#E8232B,#F0524F)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700 }}>E</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#0E1A2B' }}>Pro on the way</div>
                        <div style={{ fontSize: 11.5, color: '#6B7890', fontWeight: 600 }}>Eligio · arriving in 18 min</div>
                      </div>
                      <span style={{ width: 9, height: 9, borderRadius: '50%', background: '#15A150', boxShadow: '0 0 0 4px rgba(21,161,80,0.18)' }} />
                    </div>
                  </div>
                </div>
                <div className={s.appNewFloat}>
                  <div style={{ fontSize: 11, color: '#6B7890', fontWeight: 600 }}>Matched in</div>
                  <div style={{ fontSize: 18, fontWeight: 800, fontFamily: 'var(--display)', color: '#0E1A2B' }}>52s</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ (kept as-is) */}
      <section style={{ ...wrap, padding: '46px 20px 6px', fontFamily: 'var(--body)' }}>
        <h2 style={{ fontSize: 22, fontWeight: 800, margin: '0 0 18px', color: INK }}>Frequently asked questions</h2>
        <FaqAccordion items={CUSTOMER_FAQS} preview={6} />
        <div style={{ marginTop: 18 }}>
          <a href="/faq" style={{ color: RED, fontWeight: 700, textDecoration: 'none' }}>See all questions →</a>
        </div>
      </section>

      {/* SEO links */}
      <section style={{ ...wrap, padding: '46px 20px 60px', fontFamily: 'var(--body)' }}>
        <h2 style={{ fontSize: 22, fontWeight: 800, margin: '0 0 18px', color: INK }}>Popular services in top cities</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '8px 24px' }}>
          {SEO_SERVICES.map(([name, sl]) => (
            <div key={sl}>
              <div style={{ fontWeight: 700, margin: '10px 0 6px', color: INK }}>{name}</div>
              {TOP_CITIES.map(([cn, cs]) => (
                <a key={cs} href={`/services/${sl}/${cs}`} style={{ display: 'block', color: GREY, textDecoration: 'none', fontSize: 13, padding: '2px 0' }}>{name} in {cn}</a>
              ))}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

export default withStyles(s)(MarketplaceHome);
